import * as Sentry from '@sentry/react';
import { captureProductEvent } from './productAnalytics';

/** Operational attributes attached to Sentry breadcrumbs, spans, and workflow errors. */
export type TelemetryAttributes = Record<string, boolean | number | string | null | undefined>;

function definedAttributes(attributes: TelemetryAttributes): Record<string, boolean | number | string> {
  return Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value !== null && value !== undefined),
  ) as Record<string, boolean | number | string>;
}

/** Add context to a later error/replay without creating a standalone event. */
export function workflowBreadcrumb(action: string, attributes: TelemetryAttributes = {}): void {
  Sentry.addBreadcrumb({
    category: 'crm.workflow',
    type: 'default',
    level: 'info',
    message: action,
    data: definedAttributes(attributes),
  });
  captureProductEvent(action, attributes);
}

/** Trace a meaningful operation with timing and operational context. */
export function traceWorkflow<T>(
  operation: string,
  attributes: TelemetryAttributes,
  callback: () => T,
): T {
  return Sentry.startSpan({
    name: `crm.${operation}`,
    op: 'ui.workflow',
    attributes: definedAttributes(attributes),
  }, callback);
}

/** Report a workflow failure with the breadcrumbs that led up to it. */
export function captureWorkflowException(
  error: unknown,
  operation: string,
  attributes: TelemetryAttributes = {},
): void {
  Sentry.captureException(error, {
    tags: { component: 'crm-workflow', operation },
    extra: definedAttributes(attributes),
  });
  captureProductEvent('workflow_failed', { operation, ...attributes });
}

/** Partial completion deserves an operator-visible warning, unlike successful clicks. */
export function captureWorkflowWarning(
  operation: string,
  attributes: TelemetryAttributes = {},
): void {
  Sentry.captureMessage(`crm workflow warning: ${operation}`, {
    level: 'warning',
    tags: { component: 'crm-workflow', operation },
    extra: definedAttributes(attributes),
  });
  captureProductEvent('workflow_warning', { operation, ...attributes });
}
